import { getOptionalObjectProperty, isEmptyArray, isNullOrUndefined, vyDate } from '@vy/utils';

export enum ChartDataKeyEnum {
  DAY = 'day',
  MONTH = 'month',
}

export type ChartDataKey = ChartDataKeyEnum;

export type UsageDataItem = {
  totalValue: number;
  date: Date;
  productName: string;
};

export type GroupedByDateData = Record<string, Record<string, number>>;

export type GroupedData = {
  periodToProducts: GroupedByDateData;
  periodToDate: Record<string, Date>;
  allProducts: string[];
};

export const MIN_ROWS_IN_CHART = 12;
export const MAX_ROWS_IN_CHART = 31;

type ChartConfig = {
  dateFormatter: (date: Date) => string;
  dateKey: ChartDataKey;
  xAxisValueFormatter: (value: string) => string;
  tooltipTitle: string;
};

export const CUSTOMER_PORTAL_CHART_CONFIGS: Record<string, ChartConfig> = {
  DAILY: {
    dateFormatter: (date: Date) => vyDate(date).format('MMM DD'),
    dateKey: ChartDataKeyEnum.DAY,
    xAxisValueFormatter: (value: string) => {
      const datePart = value.split(' ')[1];
      return datePart || value;
    },
    tooltipTitle: 'Daily Usage Breakdown',
  },
  MONTHLY: {
    dateFormatter: (date: Date) => vyDate(date).format('MMM YYYY'),
    dateKey: ChartDataKeyEnum.MONTH,
    xAxisValueFormatter: (value: string) => {
      const monthPart = value.split(' ')[0];
      return monthPart || value;
    },
    tooltipTitle: 'Monthly Usage Breakdown',
  },
};

export function getGroupingFormat(dateKey: ChartDataKey): string {
  return dateKey === ChartDataKeyEnum.MONTH ? 'YYYY-MM' : 'YYYY-MM-DD';
}

export function groupDataByPeriod(data: UsageDataItem[], groupingFormat: string): GroupedData {
  const periodToProducts: GroupedByDateData = {};
  const periodToDate: Record<string, Date> = {};
  const productSet = new Set<string>();

  data.forEach((item) => {
    const period = vyDate(item.date).format(groupingFormat);

    if (isNullOrUndefined(periodToDate[period])) {
      periodToDate[period] = item.date;
    }

    if (isNullOrUndefined(periodToProducts[period])) {
      periodToProducts[period] = {};
    }

    if (isNullOrUndefined(periodToProducts[period][item.productName])) {
      periodToProducts[period][item.productName] = 0;
    }

    periodToProducts[period][item.productName] += item.totalValue;
    productSet.add(item.productName);
  });

  return {
    periodToProducts,
    periodToDate,
    allProducts: Array.from(productSet),
  };
}

export function findLastDateInRows(
  rows: Record<string, string | number | null>[],
  dateKey: ChartDataKey,
): Date {
  if (isEmptyArray(rows)) {
    return vyDate().startOf('month').toDate();
  }

  const lastRowDateString = getOptionalObjectProperty(rows[rows.length - 1], dateKey) as string;
  return new Date(lastRowDateString + ' 01');
}

export function generateFuturePeriods(
  startDate: Date,
  count: number,
  allProducts: string[],
  dateFormatter: (date: Date) => string,
  dateKey: ChartDataKey,
): Record<string, string | number | null>[] {
  const periods: Record<string, string | number | null>[] = [];

  for (let monthOffset = 1; monthOffset <= count; monthOffset++) {
    const futureDate = vyDate(startDate).add(monthOffset, 'month').toDate();
    const period = buildEmptyPeriodRow(futureDate, allProducts, dateFormatter, dateKey);
    periods.push(period);
  }

  return periods;
}

export function buildEmptyPeriodRow(
  date: Date,
  allProducts: string[],
  dateFormatter: (date: Date) => string,
  dateKey: ChartDataKey,
): Record<string, string | number | null> {
  const row: Record<string, string | number | null> = {
    [dateKey]: dateFormatter(date),
  };

  allProducts.forEach((product) => {
    row[product] = null;
  });

  return row;
}

export function createMissingPeriods(
  existingRows: Record<string, string | number | null>[],
  allProducts: string[],
  dateFormatter: (date: Date) => string,
  dateKey: ChartDataKey,
  targetCount: number,
): Record<string, string | number | null>[] {
  const periodsToAdd = targetCount - existingRows.length + 1;

  const lastExistingDate = findLastDateInRows(existingRows, dateKey);

  const missingPeriods = generateFuturePeriods(
    lastExistingDate,
    periodsToAdd,
    allProducts,
    dateFormatter,
    dateKey,
  );

  return missingPeriods;
}

export function createChartRow(
  period: string,
  groupedData: GroupedData,
  dateFormatter: (date: Date) => string,
  dateKey: ChartDataKey,
): Record<string, string | number | null> {
  const { periodToProducts, periodToDate, allProducts } = groupedData;
  const representativeDate = periodToDate[period];

  const row: Record<string, string | number | null> = {
    [dateKey]: dateFormatter(representativeDate),
  };

  allProducts.forEach((product) => {
    row[product] = getOptionalObjectProperty(periodToProducts[period], product) ?? null;
  });

  return row;
}

export function buildDataForChart(
  data: UsageDataItem[],
  dateFormatter: (date: Date) => string,
  dateKey: ChartDataKey,
): Record<string, string | number | null>[] {
  const groupingFormat = getGroupingFormat(dateKey);
  const groupedData = groupDataByPeriod(data, groupingFormat);
  const sortedPeriods = Object.keys(groupedData.periodToProducts).sort();

  const chartRows = sortedPeriods.map((period) =>
    createChartRow(period, groupedData, dateFormatter, dateKey),
  );

  if (dateKey === ChartDataKeyEnum.MONTH && chartRows.length < MIN_ROWS_IN_CHART) {
    const fillMissingPeriods = createMissingPeriods(
      chartRows,
      groupedData.allProducts,
      dateFormatter,
      dateKey,
      MIN_ROWS_IN_CHART,
    );
    chartRows.push(...fillMissingPeriods);
  }

  return chartRows;
}
